import {
  hasCharacterNamedFeature,
  resolveCharacterTotalLevel
} from '../CharacterRulesPresentation';

const COMMON_SPELL_ACTION_REFERENCES = Object.freeze({
  'cure wounds': {
    name: 'Cure Wounds',
    level: 1,
    school: 'Evocation',
    castingTime: '1 action',
    range: 'Touch',
    healing: '1d8+3',
    description: 'A creature you touch regains hit points.'
  },
  'detect magic': {
    name: 'Detect Magic',
    level: 1,
    school: 'Divination',
    castingTime: '1 action',
    range: 'Self',
    components: 'V, S',
    duration: 'Concentration, up to 10 minutes',
    description: 'For the duration, you sense the presence of magic within 30 feet of you.'
  },
  'eldritch blast': {
    name: 'Eldritch Blast',
    level: 0,
    school: 'Evocation',
    castingTime: '1 action',
    range: '120 feet',
    description: 'A beam of crackling energy streaks toward a creature within range.',
    damage: [{ dice: '1d10', type: 'force' }],
    attack: {
      type: 'rangedSpell',
      range: '120 feet'
    }
  },
  'fire bolt': {
    name: 'Fire Bolt',
    level: 0,
    school: 'Evocation',
    castingTime: '1 action',
    range: '120 feet',
    description: 'Hurl a mote of fire at a creature or object within range.',
    damage: [{ dice: '1d10', type: 'fire' }],
    attack: {
      type: 'rangedSpell',
      range: '120 feet'
    }
  },
  fireball: {
    name: 'Fireball',
    level: 3,
    school: 'Evocation',
    castingTime: '1 action',
    range: '150 feet',
    description: 'A bright streak flashes to a point you choose, then blossoms with an explosion of flame.',
    damage: [{ dice: '8d6', type: 'fire' }],
    saveType: 'dexterity',
    halfDamageOnSave: true,
    areaOfEffect: {
      shape: 'sphere',
      size: 20
    }
  },
  'healing word': {
    name: 'Healing Word',
    level: 1,
    school: 'Evocation',
    castingTime: '1 bonus action',
    range: '60 feet',
    healing: '1d4+3',
    description: 'A creature of your choice that you can see within range regains hit points.'
  },
  'mage hand': {
    name: 'Mage Hand',
    level: 0,
    school: 'Conjuration',
    castingTime: '1 action',
    range: '30 ft.',
    components: 'V, S',
    duration: '1 minute',
    utilitySpell: true,
    description: 'A spectral, floating hand appears at a point you choose within range.'
  },
  'minor illusion': {
    name: 'Minor Illusion',
    level: 0,
    school: 'Illusion',
    castingTime: '1 action',
    range: '30 feet',
    components: 'S, M',
    duration: '1 minute',
    utilitySpell: true,
    description: 'You create a sound or an image of an object within range. This spell does not make an attack roll or deal damage.'
  },
  'find familiar': {
    name: 'Find Familiar',
    level: 1,
    school: 'Conjuration',
    castingTime: '1 hour',
    range: '10 feet',
    components: 'V, S, M',
    duration: 'Instantaneous',
    utilitySpell: true,
    description: 'You gain the service of a familiar.'
  },
  'magic missile': {
    name: 'Magic Missile',
    level: 1,
    school: 'Evocation',
    castingTime: '1 action',
    range: '120 feet',
    description: 'You create three glowing darts of magical force. Each dart hits a creature of your choice that you can see within range.',
    damage: [{ dice: '1d4+1', type: 'force' }]
  },
  'misty step': {
    name: 'Misty Step',
    level: 2,
    school: 'Conjuration',
    castingTime: '1 bonus action',
    range: 'Self',
    components: 'V',
    duration: 'Instantaneous',
    description: 'Briefly surrounded by silvery mist, you teleport up to 30 feet to an unoccupied space you can see.'
  },
  'charm person': {
    name: 'Charm Person',
    level: 1,
    school: 'Enchantment',
    castingTime: '1 action',
    range: '30 ft.',
    components: 'V, S',
    duration: '1 hour',
    saveType: 'wisdom',
    description: 'One Humanoid you can see makes a Wisdom save. On a failure, it is Charmed by you until the spell ends, you or your allies damage it, or you force another save through hostile behavior.'
  },
  'detect thoughts': {
    name: 'Detect Thoughts',
    level: 2,
    school: 'Divination',
    castingTime: '1 action',
    range: 'Self',
    components: 'V, S, M',
    duration: 'Concentration, up to 1 minute',
    concentration: true,
    description: 'Read surface thoughts, then use a Magic Action on later turns to probe more deeply or shift your attention as the spell allows.'
  },
  invisibility: {
    name: 'Invisibility',
    level: 2,
    school: 'Illusion',
    castingTime: '1 action',
    range: 'Touch',
    components: 'V, S, M',
    duration: 'Concentration, up to 1 hour',
    concentration: true,
    description: 'A creature you touch has the Invisible condition until the spell ends; the effect ends early after it makes an attack roll, deals damage, or casts a spell.'
  },
  prestidigitation: {
    name: 'Prestidigitation',
    level: 0,
    school: 'Transmutation',
    castingTime: '1 action',
    range: '10 feet',
    components: 'V, S',
    duration: 'Up to 1 hour',
    description: 'This spell is a minor magical trick that novice spellcasters use for practice.'
  },
  shield: {
    name: 'Shield',
    level: 1,
    school: 'Abjuration',
    castingTime: '1 reaction',
    range: 'Self',
    description: 'An invisible barrier of magical force appears and protects you until the start of your next turn.'
  },
  'shocking grasp': {
    name: 'Shocking Grasp',
    level: 0,
    school: 'Evocation',
    castingTime: '1 action',
    range: 'Touch',
    components: 'V, S',
    duration: 'Instantaneous',
    description: 'Make a melee spell attack against a creature you can touch. On a hit, it takes lightning damage and cannot make Opportunity Attacks until the start of its next turn.',
    damage: [{ dice: '1d8', type: 'lightning' }],
    requiresAttackRoll: true,
    attack: {
      type: 'meleeSpell',
      range: 'Touch'
    }
  }
});

export const normalizeSpellActionLookupKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[-_\s]+/g, ' ')
  .replace(/[^\w\s]/g, '')
  .trim();

const normalizeDamageArray = (entry) => {
  if (Array.isArray(entry?.damage) && entry.damage.length > 0) {
    return entry.damage;
  }

  if (typeof entry?.damage === 'string' && entry.damageType) {
    return [{ dice: entry.damage, type: entry.damageType }];
  }

  return undefined;
};

const formatFeet = (value) => String(value || '')
  .replace(/\b(\d+)\s*(?:feet|foot|ft)(?:\.)?/gi, '$1 ft.')
  .replace(/\.\./g, '.');

const formatSpellRange = (value) => formatFeet(value)
  .replace(/\bself\b/gi, 'Self')
  .replace(/\btouch\b/gi, 'Touch');

const abilityModifier = (subject, ability) => {
  const aliases = ability === 'intelligence' ? ['intelligence', 'int']
    : ability === 'wisdom' ? ['wisdom', 'wis']
      : ['charisma', 'cha'];
  const branches = [subject, subject?.data, subject?.schemaData, subject?.canonicalData].filter(Boolean);
  for (const branch of branches) {
    for (const key of aliases) {
      const score = Number(branch?.abilities?.[key]);
      if (Number.isFinite(score)) return Math.floor((score - 10) / 2);
    }
  }
  return null;
};

const spellcastingAbility = (subject) => {
  const explicit = String(subject?.spellcasting?.ability || subject?.spells?.ability || '').toLowerCase();
  if (['intelligence', 'wisdom', 'charisma'].includes(explicit)) return explicit;
  const identity = [
    subject?.class,
    subject?.subclass,
    ...(Array.isArray(subject?.classes) ? subject.classes.flatMap((entry) => [entry?.name, entry?.subclass]) : [])
  ].filter(Boolean).join(' ').toLowerCase();
  if (/(arcane trickster|eldritch knight|wizard|artificer)/.test(identity)) return 'intelligence';
  if (/(cleric|druid|ranger)/.test(identity)) return 'wisdom';
  if (/(bard|paladin|sorcerer|warlock)/.test(identity)) return 'charisma';
  return null;
};

const proficiencyBonus = (subject) => {
  const explicit = Number(subject?.proficiencyBonus ?? subject?.combatStats?.proficiencyBonus ?? subject?.data?.combatStats?.proficiencyBonus);
  if (Number.isFinite(explicit)) return explicit;
  return Math.floor((Math.max(1, resolveCharacterTotalLevel(subject)) - 1) / 4) + 2;
};

const scaledCantripDamage = (damage, totalLevel) => {
  const multiplier = totalLevel >= 17 ? 4 : totalLevel >= 11 ? 3 : totalLevel >= 5 ? 2 : 1;
  return Array.isArray(damage) ? damage.map((entry) => {
    const match = String(entry?.dice || '').match(/^(\d+)d(\d+)$/i);
    return match ? { ...entry, dice: `${multiplier * Number(match[1])}d${match[2]}` } : entry;
  }) : damage;
};

const acquisitionLabel = (entry, subject, lookupKey) => {
  const explicit = entry?.acquisition || entry?.grantedBy || entry?.source?.name || entry?.source;
  if (explicit) return typeof explicit === 'string' ? explicit : String(explicit?.name || 'Recorded source');
  if (lookupKey === 'detect thoughts' && hasCharacterNamedFeature(subject, 'Telepathic')) {
    return 'Telepathic feat - 1 free cast per Long Rest; spell slots also allowed';
  }
  if (Number(entry?.level ?? entry?.spellLevel) === 0) return 'Cantrip';
  if (entry?.atWill) return 'At will';
  if (entry?.alwaysPrepared) return 'Always prepared';
  if (entry?.prepared) return 'Prepared';
  if (entry?.__known) return 'Known spell';
  return 'Source not recorded';
};

const shouldPreferReferenceSpellName = (providedName) => {
  const normalizedName = String(providedName || '').trim();
  if (!normalizedName) return true;
  if (normalizedName.includes('-') || normalizedName.includes('_')) return true;
  return normalizedName === normalizedName.toLowerCase();
};

const enrichParsedSpellMechanics = (entry) => {
  const enriched = { ...entry };
  const description = String(entry?.description || entry?.desc || entry?.text || '');

  if (!normalizeDamageArray(enriched) && description) {
    const damage = Array.from(description.matchAll(/(\d+d\d+(?:\s*[+-]\s*\d+)?)\s+([a-z]+)\s+damage/gi))
      .map((match) => ({
        dice: String(match[1] || '').replace(/\s+/g, ''),
        type: String(match[2] || '').toLowerCase()
      }));
    if (damage.length > 0) enriched.damage = damage;
  }

  const attackMatch = description.match(/\b(melee|ranged) spell attack\b/i);
  if (attackMatch && !enriched.attack?.type) {
    enriched.requiresAttackRoll = true;
    enriched.attack = {
      ...(enriched.attack || {}),
      type: attackMatch[1].toLowerCase() === 'melee' ? 'meleeSpell' : 'rangedSpell'
    };
  }

  if (!enriched.save && !enriched.savingThrow && !enriched.saveType) {
    const saveMatch = description.match(/\b(strength|dexterity|constitution|intelligence|wisdom|charisma)\s+saving throw\b/i);
    if (saveMatch) enriched.saveType = saveMatch[1].toLowerCase();
  }
  if (enriched.halfDamageOnSave == null && /half as much damage|half damage on (?:a )?successful save/i.test(description)) {
    enriched.halfDamageOnSave = true;
  }

  return enriched;
};

export const enrichSpellActionData = (spellEntry, options = {}) => {
  if (!spellEntry || typeof spellEntry !== 'object') {
    return spellEntry;
  }

  const lookupKey = normalizeSpellActionLookupKey(spellEntry.name || spellEntry.id);
  const reference = lookupKey ? COMMON_SPELL_ACTION_REFERENCES[lookupKey] : null;
  if (!reference) {
    return enrichParsedSpellMechanics(spellEntry);
  }

  const merged = {
    ...reference,
    ...spellEntry,
    name: shouldPreferReferenceSpellName(spellEntry.name)
      ? reference.name
      : (spellEntry.name || reference.name)
  };
  const preservesRecordedMechanics = spellEntry.isCustom === true
    || spellEntry.customized === true
    || spellEntry.preserveRecordedMechanics === true
    || spellEntry.rulesOverride === true;
  if (!preservesRecordedMechanics) {
    ['castingTime', 'range', 'components', 'duration'].forEach((field) => {
      if (String(reference[field] ?? '').trim()) merged[field] = reference[field];
    });
  }
  ['castingTime', 'range', 'components', 'duration', 'description', 'school'].forEach((field) => {
    if (!String(merged[field] ?? '').trim() && String(reference[field] ?? '').trim()) {
      merged[field] = reference[field];
    }
  });
  if (reference.concentration === true) merged.concentration = true;

  const subject = options?.subject || null;
  if (subject && lookupKey === 'mage hand' && hasCharacterNamedFeature(subject, 'Telekinetic')) {
    merged.range = '60 ft.';
    merged.components = 'None';
    merged.modification = 'Telekinetic: range increased by 30 ft.; no Verbal or Somatic components.';
  }

  const attackType = String(merged?.attack?.type || '').toLowerCase();
  if (subject && (merged.requiresAttackRoll === true || attackType.includes('spell'))) {
    const ability = spellcastingAbility(subject);
    const modifier = ability ? abilityModifier(subject, ability) : null;
    if (modifier != null) merged.attackBonus = modifier + proficiencyBonus(subject);
  }

  merged.range = formatSpellRange(merged.range);
  merged.duration = formatFeet(merged.duration);
  merged.description = formatFeet(merged.description);
  const acquisition = acquisitionLabel(merged, subject, lookupKey);
  if (acquisition) merged.acquisition = acquisition;

  const providedDamage = normalizeDamageArray(spellEntry);
  const referenceDamage = normalizeDamageArray(reference);
  if (providedDamage || referenceDamage) {
    merged.damage = providedDamage || referenceDamage;
  }
  if (reference?.level === 0 && normalizeDamageArray(merged)) {
    merged.damage = scaledCantripDamage(normalizeDamageArray(merged), resolveCharacterTotalLevel(subject || options));
  }

  const providedAttackType = String(spellEntry?.attack?.type || '').toLowerCase();
  const referenceAttackType = String(reference?.attack?.type || '').toLowerCase();
  if (referenceAttackType.includes('spell') && providedAttackType && !providedAttackType.includes('spell')) {
    merged.attack = {
      ...spellEntry.attack,
      ...reference.attack
    };
  }

  if (!merged.activationType && merged.castingTime) {
    merged.activationType = merged.castingTime;
  }
  if (!merged.actionType && merged.castingTime) {
    merged.actionType = merged.castingTime;
  }

  return enrichParsedSpellMechanics(merged);
};

export default enrichSpellActionData;
